import {
  LineChart,
  formatPercentage,
  PercentageValue,
  CurrencyValue,
  formatCurrency
} from '@blockanalitica/ui';
import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartFillDatasetsDates } from '@/hooks';

export default function HistoricRatesGraphs({
  rateType,
  activeDisplayOption,
  chartDaysAgo,
  data
}: {
  rateType: string;
  activeDisplayOption: string;
  chartDaysAgo: string;
  data: SimpleChartData[];
}) {
  const config: ChartConfig = useMemo(() => {
    return {
      options: {
        aspectRatio: 2.3
      }
    };
  }, []);

  const theme = useTheme();

  const valueFormatter = useCallback(
    (value: string | number) => {
      switch (activeDisplayOption) {
        case 'price':
          return (
            <CurrencyValue
              value={value}
              variant="dynamic"
              options={{
                currency: 'USD'
              }}
            />
          );
        default:
          return <PercentageValue value={value} variant="dynamic" />;
      }
    },
    [activeDisplayOption]
  );

  const yScaleValueFormatter = useCallback(
    (value: string | number) => {
      switch (activeDisplayOption) {
        case 'price':
          return formatCurrency(Number(value), { maximumFractionDigits: 5 });
        default:
          return formatPercentage(Number(value));
      }
    },
    [activeDisplayOption]
  );

  const datasets: SimpleChartDataset[] = useMemo(() => {
    switch (activeDisplayOption) {
      case 'price':
        return [
          {
            xField: 'date',
            yField: 'underlying_price',
            label: 'Price'
          }
        ];
      case 'utilization':
        return [
          {
            xField: 'date',
            yField: 'utilization_rate',
            label: 'Utilization'
          }
        ];
      default:
        return [
          {
            xField: 'date',
            yField: `supply_apy${rateType}`,
            label: 'Supply APY',
            borderColor: theme.colors.primary
          },
          {
            xField: 'date',
            yField: `borrow_apy${rateType}`,
            label: 'Borrow APY',
            borderColor: theme.colors.secondary
          }
        ];
    }
  }, [
    rateType,
    theme.colors.primary,
    theme.colors.secondary,
    activeDisplayOption
  ]);

  const chartData = useChartFillDatasetsDates(data, datasets, chartDaysAgo);

  return (
    <LineChart
      key={`${activeDisplayOption}supply_apy${rateType}borrow_apy${rateType}`}
      data={chartData}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      config={config}
      daysAgo={chartDaysAgo}
    />
  );
}
